import { useMemo, useId, useEffect, type FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import StandardDropdown from '@wix/da-ds/pkg/Dropdown/Preset/StandardDropdown';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import {
  getVideoCreatorAspectRatio,
  getVideoCreatorDuration,
  getVideoCreatorModel,
  getVideoCreatorSound,
} from '../../../../selectors/studio';
import { useDispatch, useSelector } from 'react-redux';
import { getModelPropertiesByType } from '../../ModelProperties';
import { VideoAspectRatio } from '../../../../types/store';
import {
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
} from '../../../../actions/studio/videoCreator';

import s from './VideoProperties.scss';
import sControl from '../VideoCreatorControls.scss';

export interface Props {}

const VideoProperties: FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const audioId = useId();
  const isMobile = false;
  const selectedModel = useSelector(getVideoCreatorModel);
  const aspectRatio = useSelector(getVideoCreatorAspectRatio);
  const duration = useSelector(getVideoCreatorDuration);
  const hasSound = useSelector(getVideoCreatorSound);

  const model = useMemo(() => {
    return getModelPropertiesByType(selectedModel);
  }, [selectedModel]);
  const showAudio = useMemo(() => {
    return model?.hasSound ?? false;
  }, [model]);
  const aspectRatios = useMemo(() => {
    return model?.availableAspectRatios ?? [];
  }, [model]);
  const durations = useMemo(() => {
    return model?.availableDurations ?? [];
  }, [model]);

  const isValidAspectRatio = useMemo(() => {
    if (!model || !aspectRatio) return false;
    return model.availableAspectRatios.includes(aspectRatio);
  }, [model, aspectRatio]);

  const isValidDuration = useMemo(() => {
    if (!model || !duration) return false;
    return model.availableDurations.includes(duration);
  }, [model, duration]);

  useEffect(() => {
    if (!model) return;

    if (!isValidAspectRatio && model.availableAspectRatios.length > 0) {
      dispatch(
        updateVideoCreatorAspectRatio(
          model.availableAspectRatios[0] as VideoAspectRatio
        )
      );
    }

    if (!isValidDuration && model.availableDurations.length > 0) {
      dispatch(
        updateVideoCreatorDuration(model.availableDurations[0] as number)
      );
    }

    if (!model.hasSound) {
      dispatch(updateVideoCreatorSound(false));
    }
  }, [dispatch, model, isValidAspectRatio, isValidDuration]);

  const displayAspectRatio = useMemo(() => {
    if (!isValidAspectRatio && model?.availableAspectRatios.length > 0) {
      return model.availableAspectRatios[0];
    }
    return aspectRatio;
  }, [isValidAspectRatio, model, aspectRatio]);

  const displayDuration = useMemo(() => {
    if (!isValidDuration && model?.availableDurations.length > 0) {
      return model.availableDurations[0];
    }
    return duration;
  }, [isValidDuration, model, duration]);

  return (
    <div className={s['root']}>
      <div className={s['aspect']}>
        <label
          className={sControl['control-label']}
          htmlFor="video-creator-prompt"
        >
          {t('studio.videoCreator.label.aspect')}
        </label>
        <div className={s['aspect-dropdown']}>
          <StandardDropdown
            key={`aspect-${model?.id || 'none'}-${displayAspectRatio}`}
            role="menu"
            selectedValue={displayAspectRatio}
            items={aspectRatios.map(ar => ({
              value: ar,
              label: ar,
            }))}
            onSelect={value => {
              dispatch(
                updateVideoCreatorAspectRatio(value as VideoAspectRatio)
              );
            }}
            className={s['dropdown']}
            toggleClassName={s['dropdown-toggle']}
            arrowClassName={s['dropdown-arrow']}
            placement="bottom-end"
          />
        </div>
      </div>

      <div className={s['duration']}>
        <label
          className={sControl['control-label']}
          htmlFor="video-creator-prompt"
        >
          {t('studio.videoCreator.label.duration')}
        </label>
        <div className={s['duration-dropdown']}>
          <StandardDropdown
            key={`duration-${model?.id || 'none'}-${displayDuration}`}
            role="menu"
            selectedValue={displayDuration}
            items={durations.map(d => ({
              value: d,
              label: `${d}s`,
            }))}
            onSelect={value => {
              dispatch(updateVideoCreatorDuration(value as number));
            }}
            className={s['dropdown']}
            toggleClassName={s['dropdown-toggle']}
            arrowClassName={s['dropdown-arrow']}
            placement="bottom-end"
          />
        </div>
      </div>

      {showAudio && (
        <div className={s['audio']}>
          <label className={cn(s['audio-label'], sControl['control-label'])}>
            {t('studio.videoCreator.label.audio')}
          </label>
          <div className={s['audio-toggle']}>
            <label className={s['audio-detail']} id={audioId}>
              {t('studio.videoCreator.label.audioDetail')}
            </label>
            <MainToggle
              className={s['toggle']}
              variant="main"
              size={isMobile ? 'large' : 'small'}
              value={hasSound}
              onChange={value => {
                dispatch(updateVideoCreatorSound(value));
              }}
              aria-labelledby={audioId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

VideoProperties.displayName = 'VideoProperties';
export default VideoProperties;
